<template>
  <div class="shop-page" :class="rootClasses">
    <div class="shop-page__filters">
      <div class="shop-page__filter-sticky">
        <div class="shop-page__filter-section shop-page__filter-section--large">
          <div class="shop-page__filter-section-header">
          <span class="shop-page__filter-section-title shop-page__filter-section-title--large">
            Filter
          </span>
            <button class="shop-page__filter-section-icon shop-page__filter-section-icon--toggle" @click="filterExpanded = !filterExpanded">
              <icon-expand class="shop-page__toggle-icon" />
            </button>
          </div>
        </div>
        <div class="shop-page__filter-section shop-page__filter-section--sale">
          <div class="shop-page__filter-section-header">
            <div class="shop-page__filter-section-icon">
              <icon-badge-percent />
            </div>
            <div class="shop-page__filter-section-title">
            <span>
              On Sale
            </span>
              <bool-toggle
                v-if="initDone"
                label-true="On"
                label-false="Off"
                :initial-value="searchStore.filters.sale"
                @on-change="changeSaleFilter($event)"/>
            </div>
          </div>
        </div>
        <check-list
          v-if="initDone"
          list-name="Categories"
          filter-key="categories"
          @filter-change-event="filterChangeEvent = true">
          <icon-categories />
        </check-list>
        <check-list
          v-if="initDone"
          list-name="Tags"
          filter-key="tags"
          @filter-change-event="filterChangeEvent = true">
          <icon-tags />
        </check-list>
      </div>
    </div>
    <div class="shop-page__main">
      <shop-header
        v-if="showAdditionalContent"
        class="shop-page__header-content"
        :active-tree="pageType" />
      <div class="shop-page__main-content">
        <ul class="shop-page__main-breadcrumbs" v-if="!showAdditionalContent">
          <li>
            <nuxt-link to="/">Home</nuxt-link>
          </li>
          <li>&roarr;</li>
          <li>
            <nuxt-link to="/shop">Shop</nuxt-link>
          </li>
        </ul>
        <div class="shop-page__sticky-content" ref="stickyContent">
          <div class="shop-page__search-bar-container">
            <search-bar
              bar-size="large"
              item-position="search"
              @on-search="searchStore.addFreeText($event.input)" />
          </div>
          <div class="shop-page__sticky-search-meta">
            <div class="shop-page__sticky-count" v-if="searchStatus === 1">
              Loading results {{ flatSearchResults?.length > 0 ? 'for' : '...' }}
            </div>
            <div class="shop-page__sticky-count" v-else-if="totalResults >= 0">
              {{ totalResults.toLocaleString() }} {{ totalResults === 1 ? 'result' : 'results' }} {{ flatSearchResults?.length > 0 ? 'for' : '' }}
            </div>
            <div class="shop-page__sticky-pills-wrapper">
              <filter-pills class="shop-page__sticky-pills" />
            </div>
            <shop-sort-dropdown class="shop-page__sticky-sort" @filter-change-event="filterChangeEvent = true" />
          </div>
        </div>
        <div class="shop-page__badges">
          <filter-pills />
          <shop-sort-dropdown />
        </div>

        <ul class="shop-page__results" v-if="searchResults">
          <li v-for="searchResult in searchResults" :key="searchResult.id">
            <product-tile
              :product-id="searchResult.databaseId"
              :product-name="searchResult.name"
              :product-link="searchResult.uri"
              :product-image="searchResult.image?.mediaItemUrl"
              :vendor-name="searchResult.vendor?.name"
              :vendor-slug="searchResult.vendor?.slug"
              :default-currency="searchResult.defaultCurrency"
              :sale-badges="searchResult.productSaleBadges"
              :product-variations="searchResult.variations?.nodes"
              :product-subscription-options="searchResult.productSubscriptionOptions"
              :product-specs="searchResult.productSpecs"
              :soundcloud-id="searchResult.productSpecs?.productSoundcloudCode"
              :soundcloud-id-type="searchResult.productSpecs?.soundcloudShortcode?.includes('/playlists/') ? 'playlist' : 'track'" />
          </li>
        </ul>
        <shop-pagination
          v-if="searchStatus === 2 && totalResults > 0"
          :key="searchStore.meta.currentPage"
          @to-page="changePage"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import IconBadgePercent from '~/assets/icons/IconBadgePercent.svg';
import IconCaretDown from '~/assets/icons/IconCaretDown.svg';
import IconCategories from '~/assets/icons/IconCategories.svg';
import IconTags from '~/assets/icons/IconTags.svg';
import IconVendors from '~/assets/icons/IconVendors.svg';
import IconClose from '~/assets/icons/IconClose.svg';
import BoolToggle from "~/components/UI/BoolToggle.vue";
import CheckList from "~/components/UI/CheckList.vue";
import DropDown from "~/components/UI/DropDown.vue";
import ShopSortDropdown from "~/components/shop/ShopSortDropdown.vue";
import ShopPagination from "~/components/shop/ShopPagination.vue";
import FilterPills from "~/components/shop/FilterPills.vue";
import { checkRedirectNecessary, defaultShopRoute } from '~/common/ts/storeHelpers';
import { setProductCategoryMeta } from '~/composables/page';
import { escapeJsonLd, listingCanonical } from '~/utils/seoHead';
import {
  computed,
  useHead,
  useRoute,
  useRouter,
  useHeadSafe,
  tryUseNuxtApp,
  useSeoMeta,
  defineAsyncComponent,
  ref,
  toRaw,
  useLoadingIndicator,
  useSearchStore,
  useRuntimeConfig
} from '#imports';
import { parseVendorDescription, generateVendorJsonLd } from '~/utils/vendorMeta';

const ShopHeader = () => import('~/components/shop/ShopHeader.vue');
const IconExpand = defineAsyncComponent(() => import('~/assets/icons/IconExpand.svg'));

export default {
  async setup () {
    const app = tryUseNuxtApp()
    const route = useRoute()
    const config = useRuntimeConfig()

    // Yoast points page 2+ back to page 1, so listing canonicals are built here
    useHead({
      link: [{
        rel: 'canonical',
        key: 'canonical',
        href: computed(() => listingCanonical(config.public.baseUrl, route.path, route.query.page)),
      }],
    })

    if (import.meta.server) {
      if (app) {
        app.runWithContext(async () => {
          await setProductCategoryMeta();
        })
      }
    }

    return {
      searchStore: useSearchStore(),
      route,
      router: useRouter(),
    };
  },
  created () {
    this.initPage()
  },
  mounted() {
    const mainMenu = document.querySelector('header');
    const topSize = mainMenu.getBoundingClientRect().height;
    this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      this.isSticky = !entries[0].isIntersecting;
    }, {
      rootMargin: `-${topSize + 1}px 0px 0px 0px`,
      threshold: [0.99],
    });
    this.observer.observe(this.$refs.stickyContent);
  },
  beforeUnmount() {
    this.observer?.disconnect();
  },
  components: {
    FilterPills,
    ShopHeader,
    ShopPagination,
    ShopSortDropdown,
    DropDown,
    CheckList,
    BoolToggle,
    IconBadgePercent,
    IconCaretDown,
    IconCategories,
    IconTags,
    IconVendors,
    IconClose,
    IconExpand,
  },
  watch: {
    $route (newRoute, oldRoute) {
      // do not init if filters are applied or paginated
      // but re-init if user navigates through breadcrumbs
      if (! this.filterChangeEvent) {
        this.initPage();
      }

      this.filterChangeEvent = false;
    },
    'searchStore.filters': {
      handler () { this.updateQueryParams() },
      deep: true,
    },
    'searchStore.urlFilters': {
      handler (newValue) {
        const newCategories = toRaw(newValue.categories);
        const newVendor = toRaw(newValue.vendor);
        if (newCategories.length === 0 && !newVendor && !['/shop', '/shop/'].includes(this.route.path)) {
          this.redirectToSearchHome();
        } else if (newCategories.length > 0) {
          this.updateCategoryRoutes(newCategories);
        }
      },
      deep: true,
    },
    'searchStore.meta.userOrderBy' () {
      this.updateQueryParams();
    },
    'searchStore.data.currentPage' (currentPage: string) {
      const app = tryUseNuxtApp()
      if (app) {
        app.runWithContext(() => {
          const config = useRuntimeConfig();
          const pageUrl = listingCanonical(config.public.baseUrl, this.route.path, this.route.query.page);
          const fallbackImage = `${config.public.baseUrl}/assets/images/a_sound_effect-logo.png`;
    
          // Add OpenGraph tags for vendor pages
          if (currentPage && this.pageType === 'sound-designer') {
            const image = currentPage?.image?.mediaItemUrl ?? null;
            const description = currentPage?.descriptionSeo ?? '';
    
            // Fallback to A Sound Effect logo if no vendor image
            const ogImage = image || fallbackImage;
            const vendorName = currentPage.name;
    
            // Set OpenGraph and Twitter meta tags
            useSeoMeta({
              title: `${vendorName} on A Sound Effect`,
              ogTitle: `${vendorName} on A Sound Effect`,
              twitterTitle: `${vendorName} on A Sound Effect`,
              description: description,
              ogDescription: description,
              twitterDescription: description,
              ogType: 'profile',
              ogUrl: pageUrl,
              ogLocale: 'en_US',
              ogSiteName: 'A Sound Effect',
              twitterSite: 'A Sound Effect',
              twitterCard: 'summary_large_image',
              ogImage: ogImage,
              twitterImage: ogImage,
              twitterImageAlt: vendorName,
            });
    
            // Generate and add JSON-LD structured data
            const jsonLd = generateVendorJsonLd({
              name: vendorName,
              description: description,
              image: ogImage,
              url: pageUrl,
            });
    
            useHeadSafe({
              script: [{
                id: 'json-ld-vendor',
                type: 'application/ld+json',
                textContent: escapeJsonLd(jsonLd),
              }],
            });
          } else if (currentPage) {
            const image = currentPage?.image?.mediaItemUrl;
            const description = currentPage?.descriptionSeo ?? '';

            // Fallback to A Sound Effect logo if no page image
            const ogImage = image || fallbackImage;

            useSeoMeta({
              title: `${currentPage.name} on A Sound Effect`,
              ogTitle: `${currentPage.name} on A Sound Effect`,
              description: description,
              ogDescription: description,
              ogType: 'website',
              ogUrl: pageUrl,
              ogLocale: 'en_US',
              ogSiteName: 'A Sound Effect',
              ogImage: ogImage,
            });
          } else if (this.$route.name) {
    
            let title = `${this.$route.name} on A Sound Effect`
    
            if (this.$route.name === 'shop') {
              title = 'Shop for Sound Effects on A Sound Effect';
            }
    
            useSeoMeta({
              title: title,
              ogTitle: title,
              description: '',
              ogDescription: '',
              ogType: 'website',
              ogUrl: pageUrl,
              ogLocale: 'en_US',
              ogSiteName: 'A Sound Effect',
              ogImage: fallbackImage,
            });
          }
        })
      }
    }
  },
  data () {
    return {
      filterExpanded: true,
      initDone: false,
      isSticky: true,
      observer: null as IntersectionObserver|null,
      filterChangeEvent: false,
    };
  },
  computed: {
    rootClasses () {
      return {
        'shop-page--filter-expanded': this.filterExpanded,
        'shop-page--filter-reduced': !this.filterExpanded,
        'shop-page--searching': this.searchStore.meta.searchStatus === 1,
        'shop-page--sticky': this.isSticky,
        'shop-page--without-filters': this.searchStore.flatSearchResults.length === 0,
      };
    },
    pageType () {
      const { pageParams } = this.route.params;
      return pageParams?.length > 0
        ? pageParams[0]
        : null;
    },
    showAdditionalContent () {
      return ['sound-designer', 'category', 'tag'].includes(this.pageType);
    },
    flatSearchResults () {
      return this.searchStore.flatSearchResults;
    },
    totalResults () {
      return this.searchStore.meta.totalResults;
    },
    searchResults () {
      return this.searchStore.data.results;
    },
    searchStatus () {
      return this.searchStore.meta.searchStatus;
    },
  },
  methods: {
    async initPage () {
      this.initDone = false;
      const { pageParams } = this.route.params;
      const needRedirectTo = checkRedirectNecessary(pageParams);

      if (needRedirectTo) {
        await this.router.replace(needRedirectTo);
        return;
      }

      this.searchStore.setInitialState(this.route);
      await this.searchStore.fetchData();

      const pageMeta = this.searchStore.data.currentPage;

      if (pageMeta) {
        if (! pageMeta.name) {
          // If the url provided category / vendor / tag does not exist then redirect to shop page
          this.router.replace(defaultShopRoute);
        }
      }

      this.initDone = true;
    },
    changeSaleFilter (isChecked) {
      this.filterChangeEvent = true;
      this.searchStore.toggleSale(isChecked);
    },
    changePage () {
      this.updateQueryParams();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    updateCategoryRoutes (categories: string[]): void {
      const newRoute = `/shop/category/${categories.join('/')}/`;
      const currentPath = this.route.path;
      if (currentPath !== newRoute) {
        this.router.push({
          path: newRoute,
          query: this.route.query,
        })
      }
    },
    updateQueryParams () {
      this.filterChangeEvent = true;

      this.router.replace({
        query: this.searchStore.getSelectedParamsAsObject(),
      });

      this.doSearch();
    },
    redirectToSearchHome () {
      const migratingCategories = this.searchStore.urlFilters.categories;

      if (migratingCategories.length > 0) {
        const mergedCategories = [
          ...this.searchStore.filters.categories,
          ...this.searchStore.urlFilters.categories,
        ];

        this.router.push({
          params: {
            pageParams: [],
          },
          query: {
            ...this.searchStore.getSelectedParamsAsObject(),
            categories: mergedCategories.join(','),
          },
        });
      } else {
        this.router.push({
          params: {
            pageParams: [],
          },
        });
      }
    },
    async doSearch () {
      const { start, finish } = useLoadingIndicator()
      start()
      await this.searchStore.fetchData();
      finish()
      this.filterChangeEvent = false;
    },
  },
};
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/typography";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.shop-page {
  $root: &;

  align-items: stretch;
  background-color: variables.$colorGreyMid;
  display: flex;
  font-size: helpers.px-to-rem(12);
  min-height: 70svh;
  width: 100%;

  &--filter-reduced {

    .check-list__result-list {
      display: none;
    }
  }

  @include mq.mobileOnly() {
    flex-flow: column;
  }

  &__filters {
    display: flex;
    flex-flow: column;
    transition: width .25s;
    width: helpers.px-to-rem(55);

    @include mq.mobileOnly() {
      width: 100%;
    }

    @include mq.aboveMobile() {
      border-right: 1px solid variables.$colorGrey;

      #{$root}--filter-expanded & {
        width: 21rem;
      }
    }
  }

  &__filter-sticky {
    position: sticky;
    top: var(--menu-height);

    @include mq.aboveMobile() {
      height: calc(100vh - var(--menu-height));
      overflow: auto;
    }
  }

  &__filter-section {
    border-bottom: 1px solid transparent;
    min-height: helpers.px-to-rem(50);
    padding: helpers.px-to-rem(12) helpers.px-to-rem(14);
    transition: border-bottom-color .25s;

    &--sale {
      align-items: center;
      display: flex;
      height: helpers.px-to-rem(40);
      justify-content: stretch;
      min-height: helpers.px-to-rem(40);
      padding: 0 helpers.px-to-rem(14);
    }

    &--large {
      height: helpers.px-to-rem(60);
    }

    #{$root}:not(#{$root}--filter-expanded) &--sale {
      background-color: #efcfb7;
    }

    #{$root}--filter-expanded & {
      border-bottom-color: variables.$colorGrey;
    }
  }

  &__filter-section-header {
    align-items: center;
    display: flex;
    gap: helpers.px-to-rem(13);
    justify-content: center;
    width: 100%;

    #{$root}--filter-expanded & {
      justify-content: flex-start;
    }
  }

  &__filter-section-icon {
    align-items: center;
    display: flex;
    flex: 0 0 helpers.px-to-rem(24);
    font-size: helpers.px-to-rem(20);
    height: helpers.px-to-rem(24);
    justify-content: center;
    width: helpers.px-to-rem(24);

    &--toggle {
      color: #000;
      margin-left: auto;

      @include mq.mobileOnly() {
        display: none;
      }
    }
  }

  &__toggle-icon {
    margin-bottom: 0;

    #{$root}--filter-expanded & {
      transform: rotate(180deg);
    }
  }

  &__filter-section-title {
    display: flex;
    font-size: helpers.px-to-rem(14);
    gap: helpers.px-to-rem(5);
    justify-content: space-between;
    width: 100%;

    @include mq.aboveMobile() {
      display: none;
    }

    #{$root}--filter-expanded & {
      display: flex;
    }

    &--large {
      @include typography.h3();

      font-weight: bold;
    }
  }

  &__filter-section-counter {
    align-items: center;
    background-color: #000;
    border-radius: helpers.px-to-rem(10);
    color: #FFF;
    display: inline-flex;
    font-size: helpers.px-to-rem(11);
    justify-content: center;
    min-width: helpers.px-to-rem(20);
    padding-right: 1px;
    height: helpers.px-to-rem(20);
  }

  &__main {
    flex: 0 1 100%;
    width: 100%;
  }

  &__header-content {
    margin-bottom: helpers.px-to-rem(32);
  }

  &__main-content {
    width: 100%;
  }

  &__sticky-content {
    background-color: variables.$colorGreyMid;
    border-bottom: 1px solid transparent;
    margin-bottom: helpers.px-to-rem(20);
    position: sticky;
    top: calc(var(--menu-height) - 1px); // hide top border
    transition: border-bottom-color .25s;
    z-index: 30;

    #{$root}--sticky & {
      border-color: variables.$colorGrey;
    }
  }

  &__search-bar-container {
    height: helpers.px-to-rem(61);
    padding: 0 2rem;
    transition: background-color .25s, border-bottom-color .25s;
    z-index: 1;

    #{$root}--sticky & {
      background-color: variables.$colorWhite;
      border-bottom: 1px solid variables.$colorGrey;

      .search-bar__container {
        border-color: transparent;
      }
    }
  }

  &__main-breadcrumbs,
  &__count,
  &__badges,
  &__results,
  &__sticky-search-meta,
  &__pagination {
    margin: 0 auto;
    max-width: variables.$containerLarge;
    padding: 0 2rem;
  }

  &__main-breadcrumbs {
    align-items: center;
    color: variables.$colorGrey;
    display: flex;
    gap: .5rem;
    height: helpers.px-to-rem(59);
    list-style: none;

    a {
      color: currentColor;
      text-decoration: none;
    }
  }

  &__sticky-search-meta {
    align-items: center;
    display: grid;
    gap: .5rem;
    grid-template: "count badges sortBy" auto / auto 1fr auto;
    min-height: helpers.px-to-rem(39);

    @include mq.mobileOnly() {
      grid-template: "left count sortBy right" helpers.px-to-rem(35) "left badges badges badges" auto / 2rem 1fr auto 2rem;
      padding: 0 0 .5rem;

      #{$root}--without-filters & {
        grid-template: "count sortBy" auto / 1fr auto;
        padding: 0 2rem;
      }
    }
  }

  &__sticky-pills,
  &__sticky-sort {

    @include mq.aboveMobile() {
      opacity: 0;
      pointer-events: none;
      transition: opacity .25s;

      #{$root}--sticky & {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  &__sticky-pills-wrapper {
    grid-area: badges;

    @include mq.mobileOnly() {
      overflow: auto;
      flex-wrap: nowrap;
      width: 100%;

      #{$root}--without-filters & {
        display: none;
      }
    }
  }

  &__sticky-pills {

    @include mq.mobileOnly() {
      width: max-content;
    }

    @include mq.aboveMobile() {
      padding: .25rem 0;
    }

    .filter-pills__item {
      white-space: nowrap;
    }
  }

  &__sticky-sort {
    grid-area: sortBy;
  }

  &__sticky-count,
  &__count {
    font-size: helpers.px-to-rem(16);
    font-weight: bold;
  }

  &__sticky-count {
    grid-area: count;
  }

  &__badges {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    margin-bottom: helpers.px-to-rem(20);
    opacity: 1;
    transition: opacity .25s;
    width: 100%;

    @include mq.mobileOnly() {
      display: none;
    }

    #{$root}--sticky & {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__results {
    display: grid;
    gap: helpers.px-to-rem(30) helpers.px-to-rem(10);
    grid-template-columns: repeat(2, 1fr);
    list-style: none;
    transition: filter .25s;

    #{$root}--searching & {
      filter: blur(.25rem);
    }

    @include mq.mq(variables.$mq-2-min) {
      grid-template-columns: repeat(3, 1fr);
    }

    @include mq.mq(variables.$mq-3-min) {
      grid-template-columns: repeat(4, 1fr);
    }

    @include mq.mq(variables.$mq-5-min) {
      grid-template-columns: repeat(5, 1fr);
    }

    @include mq.mq(variables.$mq-6-min) {
      grid-template-columns: repeat(6, 1fr);
    }
  }
}
</style>
